import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { QuotationDetailDto } from '../lib/quotationApi';
import DateInput from "./DateInput";

interface SendQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: QuotationDetailDto;
  /**
   * The vessel the Vessel Name field starts as: the page's answer (D104.12 - the quotation's own copy,
   * else the RFQ's), the one its header shows. Null when nothing names one: the field starts empty, never
   * a placeholder. The form does not work it out; the email carries whatever is in the field.
   */
  vesselName: string | null;
  /**
   * Sends the quotation — this form is the last step, SEND is the send. The
   * button stays disabled until the returned promise settles, so one click is
   * one email. The caller closes the modal on success and reports a failure
   * itself; on a failure the form stays open and filled in for another try.
   */
  onSend: (emailData: SendQuotationData) => Promise<void>;
}

export interface SendQuotationData {
  rfqNumber: string;
  date: string;
  clientName: string;
  vesselName: string;
  subject: string;
  description: string;
  /** All email addresses the quotation is sent to (e.g. captain, purchasing officer, agent). */
  recipients: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendQuotationModal: React.FC<SendQuotationModalProps> = ({
  isOpen,
  onClose,
  quotation,
  vesselName,
  onSend
}) => {
  const [formData, setFormData] = useState<SendQuotationData>({
    rfqNumber: quotation.rfqNumber || '',
    date: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD format
    clientName: quotation.customer?.customerName || '',
    vesselName: vesselName || '',
    subject: '',
    description: '',
    recipients: []
  });
  const [recipientInput, setRecipientInput] = useState('');
  const [sending, setSending] = useState(false);
  // D105.2: the form's own input checks show beside the field, never in a browser alert.
  const [recipientError, setRecipientError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  // D109.6: false while the date box holds a refused date; formData.date is then the OLD date.
  const [dateValid, setDateValid] = useState(true);
  const [dateError, setDateError] = useState('');

  /** Add the typed address(es) — comma/semicolon/space separated — to the recipient list. */
  const addRecipients = (): boolean => {
    const parts = recipientInput
      .split(/[,;\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return true;
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length) {
      setRecipientError(`Invalid email address: ${invalid.join(', ')}`);
      return false;
    }
    setRecipientError('');
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.concat(parts.filter((p) => !prev.recipients.includes(p)))
    }));
    setRecipientInput('');
    return true;
  };

  const removeRecipient = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((r) => r !== email)
    }));
  };

  const handleSubmit = async () => {
    if (!dateValid) {
      setDateError('Fix the date first: it is not a real day.');
      return;
    }
    setDateError('');
    // Fold any address still sitting in the input into the list first
    const pending = recipientInput.trim();
    let recipients = formData.recipients;
    if (pending) {
      const parts = pending.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
      const invalid = parts.filter((p) => !EMAIL_RE.test(p));
      if (invalid.length) {
        setRecipientError(`Invalid email address: ${invalid.join(', ')}`);
        return;
      }
      recipients = recipients.concat(parts.filter((p) => !recipients.includes(p)));
    }
    if (recipients.length === 0) {
      setRecipientError('Please add at least one recipient email address');
      return;
    }
    setRecipientError('');
    if (!formData.subject.trim()) {
      setSubjectError('Please enter a subject');
      return;
    }
    setSubjectError('');

    try {
      setSending(true);
      await onSend({ ...formData, recipients });
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (field: keyof SendQuotationData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      {/* Modal Container */}
      <div 
        className="bg-white rounded-lg relative w-full max-w-[610px] max-h-[90vh] overflow-y-auto flex flex-col"
        style={{
          minHeight: '600px'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">
            Send Quotation
          </h2>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center bg-[#D9D9D9] rounded hover:bg-gray-400 transition-colors"
          >
            <span className="text-slate-400 text-lg leading-none">×</span>
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex-1 px-6 pb-4">
          {/* RFQ Number and Date Row */}
          <div className="flex gap-4 mb-4">
            {/* RFQ Number */}
            <div className="flex-1">
              <label 
                className="block mb-2 text-slate-900"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '22px'
                }}
              >
                RFQ Number
              </label>
              <input
                type="text"
                value={formData.rfqNumber}
                onChange={(e) => handleInputChange('rfqNumber', e.target.value)}
                className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-slate-900 bg-white"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              />
            </div>

            {/* Date */}
            <div className="flex-1">
              <label 
                className="block mb-2 text-slate-900"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '22px'
                }}
              >
                Date
              </label>
              <div className="relative">
                <DateInput
                  value={formData.date}
                  onChange={(v) => handleInputChange('date', v)}
                  validity={(ok) => {
                    setDateValid(ok);
                    if (ok) setDateError('');
                  }}
                  className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-slate-900 bg-white"
                  style={{
                    fontFamily: 'Public Sans',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '24px'
                  }}
                />
                {dateError && (
                  <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
                    {dateError}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Client Name */}
          <div className="mb-4">
            <label 
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Client Name
            </label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-slate-900 bg-white"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
          </div>

          {/* Vessel Name */}
          <div className="mb-4">
            <label 
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Vessel Name
            </label>
            <input
              type="text"
              value={formData.vesselName}
              onChange={(e) => handleInputChange('vesselName', e.target.value)}
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md text-slate-900 bg-white"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
          </div>

          {/* Recipients */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Recipients <span className="text-red-500">*</span>
            </label>
            {formData.recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {formData.recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm"
                    style={{ fontFamily: 'Public Sans' }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-blue-500 hover:text-blue-800 leading-none"
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={recipientInput}
                onChange={(e) => {
                  setRecipientInput(e.target.value);
                  setRecipientError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRecipients();
                  }
                }}
                onBlur={() => addRecipients()}
                placeholder="captain@vessel.com, purchasing@agent.com"
                className="flex-1 px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
                style={{
                  fontFamily: 'Public Sans',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '24px'
                }}
              />
              <button
                type="button"
                onClick={addRecipients}
                className="px-4 py-2 border border-primary-500 text-primary-500 rounded-md text-sm font-semibold hover:bg-blue-50"
                style={{ fontFamily: 'Public Sans' }}
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500" style={{ fontFamily: 'Public Sans' }}>
              Add multiple addresses (e.g. captain, purchasing officer, agent) — separate with commas or press Enter.
            </p>
            {recipientError && (
              <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
                {recipientError}
              </p>
            )}
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => {
                handleInputChange('subject', e.target.value);
                setSubjectError('');
              }}
              placeholder="Enter Subject"
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
            {subjectError && (
              <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
                {subjectError}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="mb-4">
            <label 
              className="block mb-2 text-slate-900"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Write here..."
              rows={8}
              className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900 resize-none"
              style={{
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px'
              }}
            />
          </div>
        </div>

        {/* Send Button */}
        <div className="p-6 pt-0">
          <button
            onClick={handleSubmit}
            disabled={sending}
            className="w-full bg-primary-500 rounded-lg flex items-center justify-center hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors h-10"
          >
          <span 
            className="text-white"
            style={{
              fontFamily: 'Public Sans',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px'
            }}
          >
            {sending ? 'Sending...' : 'Send'}
          </span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SendQuotationModal;
